import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { changePassword } from '../lib/api';
import { ROLE_LABELS } from '../lib/auth';
import { Gear, Key, User } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function Settings() {
    const { user } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setLoading(true);
        try {
            await changePassword(currentPassword, newPassword);
            toast.success('Password changed successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 max-w-2xl" data-testid="settings-page">
            <div>
                <h1 className="page-title">Settings</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your account settings
                </p>
            </div>

            {/* Profile Info */}
            <div className="bg-card border border-border rounded-lg">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                    <User size={18} className="text-muted-foreground" />
                    <h2 className="font-medium">Profile Information</h2>
                </div>
                <div className="p-4 space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-muted-foreground">Name</div>
                        <div className="col-span-2 font-medium">{user?.name}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-muted-foreground">Email</div>
                        <div className="col-span-2">{user?.email}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-muted-foreground">Role</div>
                        <div className="col-span-2">{ROLE_LABELS[user?.role]}</div>
                    </div>
                </div>
            </div>

            {/* Change Password */}
            <div className="bg-card border border-border rounded-lg">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                    <Key size={18} className="text-muted-foreground" />
                    <h2 className="font-medium">Change Password</h2>
                </div>
                <form onSubmit={handleChangePassword} className="p-4 space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="current-password">Current Password</Label>
                        <Input
                            id="current-password"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            data-testid="current-password-input"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="new-password">New Password</Label>
                        <Input
                            id="new-password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={8}
                            data-testid="new-password-input"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirm New Password</Label>
                        <Input
                            id="confirm-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            data-testid="confirm-password-input"
                        />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="change-password-btn">
                        {loading ? 'Changing...' : 'Change Password'}
                    </Button>
                </form>
            </div>
        </div>
    );
}
